import { useState } from 'react';

import { useMakeModelContext } from '../../../../components/SearchPage/features/makeModel/MakeModelContext';

import { mapDisabledOptions } from '../../../../components/SearchPage/features/makeModel/MakeModel.map';
import {
  ALL_MAKES,
  ALL_MAKES_LOWERCASE,
  ALL_MODELS,
  ALL_MODELS_LOWERCASE,
  ALL_TRIMS,
} from '../../../../components/SearchPage/features/makeModel/MakeModel.constants';

import type { Options } from '../../../../components/SearchPage/features/makeModel/MakeModel.typed';
import type { ListItem } from '../../../../components/Toolkit/Inputs/CustomSelect';
import { shouldShowTrims } from './MakeModel.constants';

type UseMakeModelArgs = {
  onSelectMake: (value: string) => void;
  onSelectModel: (make: string, model: string) => void;
  onSelectTrim?: (make: string, model: string, trim: string) => void;
  maximumAmountOfMakeModelGroupsToBeDisplayed: number;
  makeOptions?: Array<Options>;
  sectionName?: string;
};

const useMakeModel = (args: UseMakeModelArgs) => {
  const {
    onSelectMake,
    onSelectModel,
    onSelectTrim,
    maximumAmountOfMakeModelGroupsToBeDisplayed,
    makeOptions,
    sectionName,
  } = args;

  const {
    makeModels,
    setIsMakeModelBottomSheetOpen,
    modelOptions,
    trimOptions,
    closeDisplay,
    modelsLoading,
    addModelOptions,
  } = useMakeModelContext();

  const [isModelSheetExpanded, setIsModelSheetExpanded] = useState(false);
  const [isTrimSheetExpanded, setIsTrimSheetExpanded] = useState(false);

  const showTrims = shouldShowTrims(sectionName);

  const makeValue =
    makeModels?.find(({ isEditable }) => isEditable)?.make || ALL_MAKES;

  const addMake = (make: ListItem, fromBottomSheet: boolean = false) => {
    const isAllMakes = make.value === '';
    const isCurrentlySelected =
      makeValue !== ALL_MAKES && make.value === makeValue;
    const deselectedMake = make.isDisabled === true || isCurrentlySelected;

    const OPEN_BOTTOMSHEET =
      showTrims && fromBottomSheet && !isAllMakes && !deselectedMake;

    onSelectMake(make.value);

    /*
     * Component is shared between mWeb (bottom sheet) and desktop
     * The model sheet should open automatically after selecting a make from the bottom sheet
     * As it's shared, we need to know the source of the action
     */
    if (OPEN_BOTTOMSHEET) {
      addModelOptions(undefined);
      setIsMakeModelBottomSheetOpen(false);
      setIsModelSheetExpanded(true);
    } else {
      closeDisplay();
    }
  };

  const selectModel = (makeValue: string) => (option: string) => {
    onSelectModel(makeValue, option);
    // When we are only selecting a single model, we want to close the model sheet after selection
    if (showTrims) {
      setIsModelSheetExpanded(false);
    }
  };

  const addModel = (option: string) => selectModel(makeValue)(option);

  const clearModels = () => selectModel(makeValue)('');

  const selectTrim =
    (makeValue: string, modelValue: string) => (option: string) => {
      if (onSelectTrim) {
        onSelectTrim(makeValue, modelValue, option);
      }
    };

  const addTrim = (option: string) => {
    const currentModel = selectedModels?.[0] || '';
    selectTrim(makeValue, currentModel)(option);
  };

  const clearTrims = () => {
    const currentModel = selectedModels?.[0] || '';
    selectTrim(makeValue, currentModel)('');
  };

  const openBottomSheet = () => setIsMakeModelBottomSheetOpen(true);
  const closeBottomSheet = () => setIsMakeModelBottomSheetOpen(false);

  let selectedModels = makeModels?.filter(
    (item) => item.make === makeValue,
  )?.[0]?.model;

  if (showTrims) {
    selectedModels = makeModels?.find(
      (item) => item.make === makeValue && item.isEditable,
    )?.model;
  }

  const multipleModels =
    selectedModels && selectedModels.length > 1
      ? ` (+${selectedModels.length - 1})`
      : '';

  const modelPlaceholder =
    selectedModels && selectedModels.length > 0 && selectedModels[0]
      ? `${selectedModels[0]}${multipleModels}`
      : ALL_MODELS;

  let selectedTrims = makeModels?.filter((item) => item.make === makeValue)?.[0]
    ?.trim;

  if (showTrims) {
    selectedTrims = makeModels?.find(
      (item) => item.make === makeValue && item.isEditable,
    )?.trim;
  }

  const multipleTrims =
    selectedTrims && selectedTrims.length > 1
      ? ` (+${selectedTrims.length - 1})`
      : '';

  const trimPlaceholder =
    selectedTrims && selectedTrims.length > 0 && selectedTrims[0]
      ? `${selectedTrims[0]}${multipleTrims}`
      : ALL_TRIMS;

  const editableMakeModelGroupAvailable = !(
    (makeModels?.length || 0) >= maximumAmountOfMakeModelGroupsToBeDisplayed &&
    !makeModels?.some(({ isEditable }) => isEditable)
  );

  const selectedModelOptions =
    selectedModels && selectedModels.length > 0 ? selectedModels : [''];
  const selectedTrimOptions =
    selectedTrims && selectedTrims.length > 0 ? selectedTrims : [''];

  const isModelFilterDisabled =
    makeValue.toLowerCase() === ALL_MAKES_LOWERCASE || !modelOptions;
  const isTrimFilterDisabled =
    makeValue.toLowerCase() === ALL_MAKES_LOWERCASE ||
    modelPlaceholder.toLowerCase() === ALL_MODELS_LOWERCASE ||
    !trimOptions;
  const isAddAnotherDisabled = makeValue.toLowerCase() === ALL_MAKES_LOWERCASE;

  // Only disable makes that have "All models" selected (no specific model)
  let disabledMakes = makeModels?.map(({ make }) => make);

  if (showTrims) {
    disabledMakes = makeModels
      ?.filter(({ model }) => !model || model.length === 0)
      .map(({ make }) => make);
  }

  // Get all selected models across all make-model combinations to prevent duplicates
  // Exclude models from the editable combination since they should remain selectable
  const allSelectedModels =
    makeModels
      ?.filter(({ isEditable }) => !isEditable)
      ?.flatMap(({ model }) => model || [])
      .filter((modelName) => modelName !== '') || [];

  const mappedMakeOptions = makeOptions
    ? mapDisabledOptions(makeOptions, disabledMakes)
    : [];

  return {
    addMake,
    clearMake: onSelectMake,
    addModel,
    clearModels,
    addTrim,
    clearTrims,
    openBottomSheet,
    closeBottomSheet,
    makeValue,
    selectedModelOptions,
    selectedTrimOptions,
    modelPlaceholder,
    trimPlaceholder,
    editableMakeModelGroupAvailable,
    isModelFilterDisabled,
    isTrimFilterDisabled,
    isAddAnotherDisabled,
    mappedMakeOptions,
    allSelectedModels,
    isModelSheetExpanded,
    setIsModelSheetExpanded,
    isTrimSheetExpanded,
    setIsTrimSheetExpanded,
    modelsLoading,
  };
};

export { useMakeModel };
